import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { APIContext } from "../../../api";
import { formatISTDateTime } from "../../../lib/admin-time";

function duration(minutes) {
  if (!minutes) return "Usage window";
  if (minutes % 1440 === 0) return `${minutes / 1440}-day limit`;
  if (minutes % 60 === 0) return `${minutes / 60}-hour limit`;
  return `${minutes}-minute limit`;
}

export function UsageCard({ provider, now, failedRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const observed = Date.parse(provider.observedAt);
  const stale = failedRefresh || !Number.isFinite(observed) || now - observed > 5 * 60_000;
  const allWindows = provider.windows || [];
  const groups = provider.adapter === "antigravity"
    ? [...new Map(allWindows.filter((w) => w.groupId).map((w) => [w.groupId, w.label])).entries()] : [];
  const activeGroup = groups.some(([id]) => id === selectedGroup) ? selectedGroup : groups[0]?.[0];
  const windows = groups.length ? allWindows.filter((w) => w.groupId === activeGroup) : allWindows;
  const available = provider.status === "available" && windows.length > 0;
  const exhausted = !stale && windows.some((w) => w.remainingPercent === 0 &&
    (!w.resetsAt || Date.parse(w.resetsAt) > now));
  const status = !available ? provider.status === "login_required" ? "Sign-in needed" : "Unavailable"
    : stale ? "Last reading" : exhausted ? "Limit reached" : "Reported";
  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4" aria-label={provider.label}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-900">{provider.label}</h4>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${exhausted
          ? "bg-red-50 text-red-700" : !available || stale ? "bg-gray-100 text-gray-600" : "bg-indigo-50 text-indigo-700"}`}>
          {status}
        </span>
      </div>
      {available ? (
        <div className="mt-4 space-y-4">
          {groups.length > 1 ? <div>
            <label htmlFor="antigravity-usage-group" className="mb-1 block text-xs text-gray-500">Shared allowance</label>
            <select id="antigravity-usage-group" value={activeGroup} onChange={(event) => setSelectedGroup(event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700">
              {groups.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div> : null}
          {(expanded ? windows : windows.slice(0, 3)).map((w) => {
            const expired = w.resetsAt && Date.parse(w.resetsAt) <= now;
            const historical = stale || expired;
            const valid = typeof w.remainingPercent === "number" && Number.isFinite(w.remainingPercent);
            const remaining = valid ? Math.max(0, Math.min(100, w.remainingPercent)) : null;
            return (
              <div key={w.id}>
                <div className="flex items-end justify-between gap-3">
                  <div className="text-xs text-gray-500">
                    <div className="font-medium text-gray-700">{w.periodLabel || duration(w.durationMinutes)}</div>
                    <div>{w.label}</div>
                  </div>
                  <div className="text-right">
                    <strong className={`text-xl ${historical ? "text-gray-500" : remaining <= 10 ? "text-red-600" : "text-gray-900"}`}>
                      {remaining === null ? "—" : `${Math.round(remaining * 100) / 100}%`}
                    </strong>
                    <div className="text-[11px] text-gray-500">{historical ? "last reported remaining" : "remaining"}</div>
                  </div>
                </div>
                {remaining !== null && !historical ? (
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100" role="progressbar"
                    aria-label={`${provider.label} ${w.label} ${w.periodLabel || duration(w.durationMinutes)} remaining`}
                    aria-valuemin={0} aria-valuemax={100} aria-valuenow={remaining}>
                    <div className={`h-full rounded-full ${remaining <= 10 ? "bg-red-500" : remaining <= 25 ? "bg-amber-500" : "bg-indigo-500"}`}
                      style={{ width: `${remaining}%` }} />
                  </div>
                ) : null}
                <div className={`mt-1.5 text-[11px] ${expired ? "text-amber-700" : "text-gray-400"}`}>
                  {expired ? "Reset time passed — awaiting a new reading" : w.resetsAt
                    ? `Resets ${formatISTDateTime(w.resetsAt)}` : "Reset time not reported"}
                </div>
              </div>
            );
          })}
          {windows.length > 3 ? <button type="button" onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
            {expanded ? "Show fewer limits" : `Show all ${windows.length} limits`}
          </button> : null}
        </div>
      ) : (
        <div className="mt-4">
          <span className="text-2xl text-gray-400">—</span>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">{provider.message || "Remaining allowance was not reported."}</p>
        </div>
      )}
      {available && provider.observedAt ? (
        <p className="mt-4 border-t border-gray-100 pt-2 text-[11px] text-gray-400">
          As of {formatISTDateTime(provider.observedAt)}
        </p>
      ) : null}
    </article>
  );
}

export default function CliAgentUsage() {
  const { getCliAgentUsage } = useContext(APIContext);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const pending = useRef(false);
  const mounted = useRef(false);
  const refresh = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    setLoading(true);
    try {
      const result = await getCliAgentUsage();
      if (!Array.isArray(result?.providers) || !result.providers.length) throw new Error("Invalid response");
      if (mounted.current) {
        setSnapshot(result);
        setError("");
      }
    } catch {
      if (mounted.current) setError("Could not refresh CLI usage. Check the usage service and try again. Any readings below are from the previous check.");
    } finally {
      pending.current = false;
      if (mounted.current) { setLoading(false); setNow(Date.now()); }
    }
  }, [getCliAgentUsage]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const poll = setInterval(() => {
      if (document.visibilityState !== "hidden") refresh();
    }, 60000);
    const clock = setInterval(() => setNow(Date.now()), 30000);
    return () => { mounted.current = false; clearInterval(poll); clearInterval(clock); };
  }, [refresh]);

  return (
    <section className="my-7 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:p-5" aria-labelledby="cli-usage-heading">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 id="cli-usage-heading" className="text-base font-semibold text-gray-900">Remaining CLI usage</h3>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500">
            Account allowances shared across CLI sessions and other tools using the same account.
            OpenAI usage is shown under Codex. Checks update every minute while this page is visible.
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={loading}
          className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-100 disabled:opacity-50">
          {loading ? "Checking…" : "Refresh usage"}
        </button>
      </div>
      {snapshot ? <p className="mt-3 text-xs text-gray-500">
        Source: <strong>{snapshot.checkedOn === "local" ? "Local machine" : "Builder machine"}</strong>
        {snapshot.machine ? ` · ${snapshot.machine}` : ""}
        {snapshot.fetchedAt ? ` · Checked ${formatISTDateTime(snapshot.fetchedAt)}` : ""}
        {snapshot.cached ? " · Cached for up to 1 minute" : ""}
      </p> : null}
      {error ? <p role="alert" className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">{error}</p> : null}
      {loading && !snapshot ? <p role="status" className="py-8 text-center text-sm text-gray-500">Reading CLI account limits…</p> : null}
      {snapshot ? <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {snapshot.providers.map((provider) => <UsageCard key={provider.adapter} provider={provider} now={now} failedRefresh={Boolean(error)} />)}
      </div> : null}
    </section>
  );
}
